import React, {useState} from 'react';
import {Form, Upload, Space, Button, Tabs, Badge} from 'antd';
import {PlusOutlined} from '@ant-design/icons';
import useHTTP from "@/hooks/useHTTP.mjs";
import runAsync from "@/script/runAsync.mjs";
import Modal from '@/components/Modal/Modal';
import Permission from '@/components/Permission.jsx';

const {TabPane} = Tabs;

/**
 * 导入需求架构 — 弹窗组件
 *
 * 上传需求分析智能体生成的三份 md 文档：
 * - 需求架构.md → 系统概要（Markdown 原文）
 * - 功能结构.md → 功能结构模型（功能域层级树）
 * - 交互入口清单.md → 入口结构（系统入口树 + 关联入口界面）
 */
const Pane = ({param = {prjId, sVer}, onOk, ...props}) => {
    const http = useHTTP();
    const [form] = Form.useForm();

    const [summaryFileList, setSummaryFileList] = useState([]);
    const [faFileList, setFaFileList] = useState([]);
    const [uaFileList, setUaFileList] = useState([]);
    const [loading, setLoading] = useState(false);

    const {prjId, sVer} = param;

    const doImport = async () => {
        if (summaryFileList.length === 0 && faFileList.length === 0 && uaFileList.length === 0) {
            return Modal.info({content: '请至少上传一个文件'});
        }

        try {
            setLoading(true);

            const files = [...summaryFileList, ...faFileList, ...uaFileList];
            const header = {headers: {'Content-Type': 'multipart/form-data'}};

            const data = await runAsync(
                async () => {
                    return await http.post('/archfas/importArchitecturePlan', {
                        prjId,
                        sVer,
                        files,
                    }, header);
                }, {
                    error: {content: (err) => `导入失败: ${err.message}`},
                    loading: {show: true},
                    success: {show: true, content: () => '导入完成'},
                }
            );

            showResult(data);
            onOk?.(true);
        } catch (err) {
            console.log(err);
        } finally {
            setLoading(false);
        }
    };

    const showResult = (data) => {
        const summaryResult = data?.summaryResult;
        const faResult = data?.faResult;
        const uaResult = data?.uaResult;

        Modal.info({
            width: 800,
            title: '导入结果',
            content: <>
                {summaryResult && <div style={{marginBottom: 12, fontWeight: 'bold'}}>{summaryResult}</div>}

                <ResultTabs
                    result={faResult}
                    labels={{adds: '新增功能域', upts: '简述已更新', exists: '已存在', deletes: '已删除', infos: '信息', invalids: '不合法'}}
                />
                <ResultTabs
                    result={uaResult}
                    labels={{adds: '新增系统入口', upts: '关联界面已更新', exists: '已存在', deletes: '已删除', infos: '信息', invalids: '不合法'}}
                />
            </>
        });
    };

    const ResultTabs = ({result, labels}) => {
        if (!result) {
            return null;
        }
        const {adds, upts, exists, deletes, infos, invalids} = result;
        return (
            <Tabs type="list" style={{overflow: 'hidden'}}>
                {adds && adds.length > 0 && (
                    <TabPane key="adds" tab={<BadgeExt name={labels.adds} count={adds.length}/>}>
                        <ResultList items={adds}/>
                    </TabPane>
                )}
                {upts && upts.length > 0 && (
                    <TabPane key="upts" tab={<BadgeExt name={labels.upts} count={upts.length}/>}>
                        <ResultList items={upts}/>
                    </TabPane>
                )}
                {exists && exists.length > 0 && (
                    <TabPane key="exists" tab={<BadgeExt name={labels.exists} count={exists.length}/>}>
                        <ResultList items={exists}/>
                    </TabPane>
                )}
                {deletes && deletes.length > 0 && (
                    <TabPane key="deletes" tab={<BadgeExt name={labels.deletes} count={deletes.length}/>}>
                        <ResultList items={deletes}/>
                    </TabPane>
                )}
                {infos && infos.length > 0 && (
                    <TabPane key="infos" tab={<BadgeExt name={labels.infos} count={infos.length}/>}>
                        <ResultList items={infos}/>
                    </TabPane>
                )}
                {invalids && invalids.length > 0 && (
                    <TabPane key="invalids" tab={<BadgeExt name={labels.invalids} count={invalids.length}/>}>
                        <ResultList items={invalids}/>
                    </TabPane>
                )}
            </Tabs>
        );
    };

    const ResultList = ({items}) => {
        return (
            <div style={{listStyle: 'none', height: '300px', overflow: 'auto'}}>
                {items.map((v, i) => <div key={i}>{v}</div>)}
            </div>
        );
    };

    const BadgeExt = ({name, count}) => {
        return <Badge size="small" offset={[10, 0]} count={count}>{name}</Badge>;
    };

    return (
        <Modal
            title="导入需求架构"
            okText="确认导入"
            cancelText="取消"
            width={550}
            bodyStyle={{
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                background: '#fff',
                padding: 24
            }}
            {...props}
            footer={[
                <Space key="footer">
                    <Button onClick={() => onOk?.()}>取消</Button>
                    <Permission permission="ARCH_FA_UPT" projects={[prjId + sVer]}>
                        <Button type="primary" onClick={doImport} loading={loading}>确认导入</Button>
                    </Permission>
                </Space>
            ]}
        >
            <Form form={form} layout="vertical">
                <Form.Item label="需求架构.md" extra="将作为系统概要直接展示 Markdown 全文">
                    <Upload
                        accept=".md"
                        fileList={summaryFileList}
                        maxCount={1}
                        beforeUpload={(file) => {
                            setSummaryFileList([file]);
                            return false;
                        }}
                        onRemove={() => {
                            setSummaryFileList([]);
                        }}
                    >
                        <div className="ant-upload-btn bg-grey-light">
                            <PlusOutlined/>
                            <div style={{marginTop: 4}}>上传 .md 文件</div>
                        </div>
                    </Upload>
                </Form.Item>

                <Form.Item label="功能结构.md" extra="解析功能域表格，导入为功能结构模型">
                    <Upload
                        accept=".md"
                        fileList={faFileList}
                        maxCount={1}
                        beforeUpload={(file) => {
                            setFaFileList([file]);
                            return false;
                        }}
                        onRemove={() => {
                            setFaFileList([]);
                        }}
                    >
                        <div className="ant-upload-btn bg-grey-light">
                            <PlusOutlined/>
                            <div style={{marginTop: 4}}>上传 .md 文件</div>
                        </div>
                    </Upload>
                </Form.Item>

                <Form.Item label="交互入口清单.md" extra="解析界面入口层级表，导入为入口模型">
                    <Upload
                        accept=".md"
                        fileList={uaFileList}
                        maxCount={1}
                        beforeUpload={(file) => {
                            setUaFileList([file]);
                            return false;
                        }}
                        onRemove={() => {
                            setUaFileList([]);
                        }}
                    >
                        <div className="ant-upload-btn bg-grey-light">
                            <PlusOutlined/>
                            <div style={{marginTop: 4}}>上传 .md 文件</div>
                        </div>
                    </Upload>
                </Form.Item>

                <div style={{color: '#999', fontSize: 12, marginTop: 8}}>
                    注：请上传需求分析智能体生成的标准 Markdown 文档格式
                </div>
            </Form>
        </Modal>
    );
};

export default Pane;
